import { useQuery } from '@tanstack/react-query';

import { UsersRepository } from '@didaskill/domain/data/repositories';
import type { FEATURE_FLAGS } from '@didaskill/domain/users/constants';
import type { EmployeeViewFull, UserFeatureFlag } from '@didaskill/domain/users/types';
import type { Nullable } from '@didaskill/types/misc';
import { cachePresets, type UseQueryParams } from '@didaskill/types/web';

import http from '../../../services/http/api';

const usersRepository = new UsersRepository(http);

export const useUser = (useQueryParams?: UseQueryParams<EmployeeViewFull>) =>
  useQuery({
    queryKey: ['user'],
    queryFn: () => usersRepository.getCurrentUser(),
    ...useQueryParams,
    ...cachePresets.live,
  });

export const useUserMails = (
  labelId: string,
  useQueryParams?: UseQueryParams<Awaited<ReturnType<UsersRepository['getUserMails']>>>,
) =>
  useQuery({
    queryKey: ['userMails', labelId],
    queryFn: () => usersRepository.getUserMails(labelId),
    ...useQueryParams,
    ...cachePresets.short,
  });

export const useUserFeatureFlags = (
  useQueryParams?: UseQueryParams<
    Awaited<ReturnType<UsersRepository['getCurrentUserFeatureFlags']>>
  >,
) =>
  useQuery({
    queryKey: ['user-feature-flags'],
    queryFn: () => usersRepository.getCurrentUserFeatureFlags(),
    throwOnError: false,
    ...useQueryParams,
    // We have 1 hour cache for this query in the backend via redis.
    ...cachePresets.live,
  });

export const useUserFeatureFlag = (
  featureFlag: FEATURE_FLAGS,
): Nullable<{
  isLoading: boolean;
  data: UserFeatureFlag;
}> => {
  const { data: featureFlags, isLoading } = useUserFeatureFlags();

  return {
    isLoading,
    data: (featureFlags || []).find(({ name }) => name === featureFlag) || null,
  };
};
